import { create } from 'zustand';
import {
  adTaxonomyApi,
  TaxonomyClient,
  TaxonomyData,
  TaxonomyNode,
  ReferenceAd,
  AdDetailResponse,
  getApiBase,
} from '@/services/api';

interface TaxonomyState {
  // Data
  clients: TaxonomyClient[];
  selectedClientId: string | null;
  taxonomyData: TaxonomyData | null;
  selectedNode: TaxonomyNode | null;
  filteredAds: ReferenceAd[];
  adImageUrls: Record<string, string>;
  dimensionFilter: 'all' | 'form' | 'meaning';
  searchQuery: string;

  // Ad detail modal
  selectedAdDetail: AdDetailResponse | null;
  adDetailLoading: boolean;

  // Loading
  clientsLoading: boolean;
  taxonomyLoading: boolean;
  adsLoading: boolean;
  error: string | null;

  // Actions
  fetchClients: () => Promise<void>;
  selectClient: (clientId: string) => Promise<void>;
  selectNode: (node: TaxonomyNode | null) => void;
  setDimensionFilter: (filter: 'all' | 'form' | 'meaning') => void;
  setSearchQuery: (query: string) => void;
  fetchAdsForNode: (nodeId: string) => Promise<void>;
  openAdDetail: (adId: string) => Promise<void>;
  closeAdDetail: () => void;
  reset: () => void;
}

export const useTaxonomyStore = create<TaxonomyState>((set, get) => ({
  clients: [],
  selectedClientId: null,
  taxonomyData: null,
  selectedNode: null,
  filteredAds: [],
  adImageUrls: {},
  dimensionFilter: 'all',
  searchQuery: '',

  selectedAdDetail: null,
  adDetailLoading: false,

  clientsLoading: false,
  taxonomyLoading: false,
  adsLoading: false,
  error: null,

  fetchClients: async () => {
    set({ clientsLoading: true, error: null });
    try {
      const res = await adTaxonomyApi.listClients();
      set({ clients: res.clients, clientsLoading: false });
      if (!get().selectedClientId && res.clients.length > 0) {
        get().selectClient(res.clients[0].client_id);
      }
    } catch (e: any) {
      set({ clientsLoading: false, error: e.message });
    }
  },

  selectClient: async (clientId: string) => {
    set({
      selectedClientId: clientId,
      taxonomyLoading: true,
      selectedNode: null,
      filteredAds: [],
      adImageUrls: {},
      error: null,
    });
    try {
      const data = await adTaxonomyApi.getTaxonomy(clientId);
      set({ taxonomyData: data, taxonomyLoading: false });

      // Fetch image URLs in background, prepend API base for cross-origin
      const base = getApiBase();
      adTaxonomyApi.getAdImageUrls(clientId).then((res) => {
        const urls: Record<string, string> = {};
        for (const [adId, path] of Object.entries(res.urls)) {
          urls[adId] = base ? `${base}${path}` : path;
        }
        set({ adImageUrls: urls });
      }).catch(() => {});
    } catch (e: any) {
      set({ taxonomyLoading: false, error: e.message });
    }
  },

  selectNode: (node: TaxonomyNode | null) => {
    set({ selectedNode: node, filteredAds: [] });
    if (node) {
      get().fetchAdsForNode(node.node_id);
    }
  },

  setDimensionFilter: (filter) => set({ dimensionFilter: filter }),

  setSearchQuery: (query) => set({ searchQuery: query }),

  fetchAdsForNode: async (nodeId: string) => {
    const clientId = get().selectedClientId;
    if (!clientId) return;
    set({ adsLoading: true });
    try {
      const res = await adTaxonomyApi.getAds(clientId, nodeId);
      set({ filteredAds: res.ads, adsLoading: false });
    } catch {
      set({ adsLoading: false });
    }
  },

  openAdDetail: async (adId: string) => {
    const clientId = get().selectedClientId;
    if (!clientId) return;
    set({ adDetailLoading: true, selectedAdDetail: null });
    try {
      const detail = await adTaxonomyApi.getAdDetail(clientId, adId);
      set({ selectedAdDetail: detail, adDetailLoading: false });
    } catch {
      set({ adDetailLoading: false });
    }
  },

  closeAdDetail: () => set({ selectedAdDetail: null }),

  reset: () =>
    set({
      selectedClientId: null,
      taxonomyData: null,
      selectedNode: null,
      filteredAds: [],
      adImageUrls: {},
      selectedAdDetail: null,
      dimensionFilter: 'all',
      searchQuery: '',
      error: null,
    }),
}));
